// src/features/tasks/components/GlobalTaskActionsDialog.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Typography,
  Box,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  LocalOffer as CategoryIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  EditCalendar as BackdateIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { getStateConfig, getPriorityColor } from '../utils/taskStateUtils';
import ProfilePicture from '../../../components/common/ProfilePicture';
import UncheckedEquipmentDialog from './UncheckedEquipmentDialog';

export default function GlobalTaskActionsDialog({
  dialogStates,
  dialogHandlers,
  loading
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const [lastCompletionOpen, setLastCompletionOpen] = useState(true);
  const [backdateOpen, setBackdateOpen] = useState(false);
  const completionDateInputRef = useRef(null);

  const {
    confirmDialogOpen,
    notes,
    currentTask,
    confirmConfig,
    isConfirmDisabled,
    lastCompletion,
    completionDate,
    completionDateReason,
    equipmentWarning,
    equipmentWarningBusy,
  } = dialogStates;

  const {
    handleConfirmAction,
    handleCloseConfirmDialog,
    setNotes,
    setCompletionDate,
    setCompletionDateReason,
    handleCancelEquipmentWarning,
    handleCheckAndContinue,
  } = dialogHandlers;

  // Reset the date control whenever the dialog is dismissed or reopened
  useEffect(() => {
    if (!confirmDialogOpen) setBackdateOpen(false);
  }, [confirmDialogOpen]);

  const closeBackdate = () => {
    setBackdateOpen(false);
    setCompletionDate?.(null);
    setCompletionDateReason?.('');
  };

  const equipmentWarningDialog = (
    <UncheckedEquipmentDialog
      warning={equipmentWarning}
      busy={equipmentWarningBusy}
      onCancel={handleCancelEquipmentWarning}
      onContinue={handleCheckAndContinue}
    />
  );

  if (!currentTask) return equipmentWarningDialog;

  const stateConfig = getStateConfig(currentTask.state);
  const priorityColor = getPriorityColor(currentTask.priority);
  const hasNotes = !!confirmConfig?.notesField;
  const lastStateConfig = lastCompletion ? getStateConfig(lastCompletion.state) : null;

  const TaskDetails = () => (
    <Box>
      <Box display="flex" alignItems="flex-start" gap={2} mb={2.5}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, flexShrink: 0 }}>
          <AssignmentIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3, mt: 0.5 }}>
            {currentTask.title}
          </Typography>
          <Box display="flex" gap={2} pt={1}>
            <Chip
              icon={React.createElement(stateConfig.icon)}
              label={stateConfig.label}
              size="small"
              color={stateConfig.color}
              variant="outlined"
            />
            <Chip
              label={t(`tasks:priority.${currentTask.priority?.toLowerCase()}`, { defaultValue: currentTask.priority })}
              size="small"
              color={priorityColor}
            />
          </Box>
        </Box>
      </Box>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2.5}>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {currentTask.primary_assignee?.name && (
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <PersonIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tasks:task_card.assigned_to')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.25}>
              <ProfilePicture
                profilePictureFileId={currentTask.primary_assignee.profile_picture_file_id}
                name={currentTask.primary_assignee.name}
                size={22}
                imageSize="32x32"
                color={currentTask.primary_assignee.color}
              />
              <Typography variant="body2" fontWeight={500}>
                {currentTask.primary_assignee.name}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {currentTask.category?.name && (
        <Box display="flex" alignItems="center" gap={1.5}>
          <CategoryIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tasks:task_card.category')}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {currentTask.category.name}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {equipmentWarningDialog}
      {confirmConfig && (
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCloseConfirmDialog}
          maxWidth={'md'}
          fullWidth
          slotProps={{ paper: { sx: { borderRadius: 3 } } }}
        >
          <DialogTitle sx={{  }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <Box>
                <Typography variant="h2" fontSize={"22px"}>
                  {confirmConfig.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {confirmConfig.message}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: -0.5, flexShrink: 0 }}>
                {confirmConfig.allowBackdate && (
                  <Tooltip title={t('tasks:dialogs.complete_task.backdate.tooltip')}>
                    <IconButton
                      size="small"
                      color={backdateOpen ? 'warning' : 'default'}
                      onClick={() => (backdateOpen ? closeBackdate() : setBackdateOpen(true))}
                    >
                      <BackdateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton onClick={handleCloseConfirmDialog} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 1, pb: 1 }}>
            {hasNotes ? (
              <Box display="flex" gap={3}>
                <Box sx={{ flex: 1, flexShrink: 0 }}>
                  <TaskDetails />
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box flex={2} minWidth={0}>
                  {lastCompletion && (
                    <Box
                      sx={{
                        mb: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        onClick={() => setLastCompletionOpen(o => !o)}
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          cursor: 'pointer',
                          bgcolor: (theme) => alpha(theme.palette[lastStateConfig.color]?.main ?? theme.palette.grey[500], 0.08),
                          '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette[lastStateConfig.color]?.main ?? theme.palette.grey[500], 0.13),
                          },
                        }}
                      >
                        <EventNoteIcon fontSize="medium" sx={{ flexShrink: 0, color: `${lastStateConfig.color}.dark`, mb: 5 }} />
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={500}>
                            {t(`tasks:dialogs.complete_task.last_task_state_${(lastCompletion.state ?? 'COMPLETED').toLowerCase()}`)}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.25}>
                            {lastCompletion.completion_date && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {new Date(lastCompletion.completion_date).toLocaleDateString()}
                              </Typography>
                            )}
                            {lastCompletion.completed_by && (
                              <>
                                {lastCompletion.completion_date && (
                                  <Typography variant="caption" color="text.secondary">·</Typography>
                                )}
                                <ProfilePicture
                                  profilePictureFileId={lastCompletion.completed_by.profile_picture_file_id}
                                  name={lastCompletion.completed_by.name}
                                  size={20}
                                  imageSize="32x32"
                                  color={lastCompletion.completed_by.color}
                                />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                  {lastCompletion.completed_by.name}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{
                            flexShrink: 0,
                            color: 'text.secondary',
                            transition: 'transform 0.2s',
                            transform: lastCompletionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                          }}
                        />
                      </Box>
                      <Collapse in={lastCompletionOpen}>
                        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {lastCompletion.completion_notes || t('tasks:dialogs.complete_task.no_previous_notes')}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {confirmConfig.allowBackdate && (
                    <Collapse
                      in={backdateOpen}
                      // Focus the date field once the expand transition finishes —
                      // focusing while it is still collapsed scrolls the dialog oddly
                      onEntered={() => completionDateInputRef.current?.focus()}
                    >
                      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          {t('tasks:dialogs.complete_task.backdate.requires_approval')}
                        </Alert>
                        <>
                          <DatePicker
                            label={t('tasks:dialogs.complete_task.backdate.date_label')}
                            value={completionDate || null}
                            onChange={(newValue) => setCompletionDate?.(newValue)}
                            disabled={loading}
                            format="dd/MM/yyyy"
                            maxDate={new Date()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                inputRef: completionDateInputRef,
                              },
                            }}
                          />
                        </>
                        <TextField
                          fullWidth
                          required
                          multiline
                          rows={2}
                          label={t('tasks:dialogs.complete_task.backdate.reason_label')}
                          placeholder={t('tasks:dialogs.complete_task.backdate.reason_placeholder')}
                          value={completionDateReason || ''}
                          onChange={(e) => setCompletionDateReason?.(e.target.value)}
                          slotProps={{ htmlInput: { maxLength: 1000 } }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Box>
                    </Collapse>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    rows={lastCompletion || backdateOpen ? 4 : 6}
                    required={confirmConfig.notesField.required}
                    label={confirmConfig.notesField.label}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={confirmConfig.notesField.placeholder}
                    slotProps={{ htmlInput: { maxLength: 1000 } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {t('tasks:dialogs.complete_task.character_count', { count: notes.length })}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <TaskDetails />
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleCloseConfirmDialog}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              {t('tasks:dialogs.confirm_action.cancel')}
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              disabled={loading || isConfirmDisabled}
              color={confirmConfig.severity}
              sx={{ borderRadius: 2, minWidth: 120 }}
            >
              {confirmConfig.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
